import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Container, 
  Paper, 
  Button,
  Alert,
  Card,
  CardContent
} from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { useAuth } from '@/context/AuthProvider';
import { SUBSCRIPTION_CONFIG } from '@/config';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const { user, refreshProfile, hasActiveSubscription } = useAuth();
  const navigate = useNavigate();

  const sessionId = searchParams.get('session_id');
  const flow = searchParams.get('flow');
  const isSubscriptionFlow = flow === SUBSCRIPTION_CONFIG.successFlowParam;
  const [refreshing, setRefreshing] = useState(isSubscriptionFlow);
  const [refreshError, setRefreshError] = useState<string | null>(null);

  useEffect(() => {
    if (!isSubscriptionFlow) return;
    let isMounted = true;
    (async () => {
      try {
        await refreshProfile();
        if (isMounted) {
          setRefreshError(null);
        }
      } catch (error) {
        console.error("Error refreshing profile:", error);
        if (isMounted) {
          setRefreshError("We could not confirm your subscription automatically. Please refresh the page in a moment.");
        }
      } finally {
        if (isMounted) {
          setRefreshing(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [isSubscriptionFlow, refreshProfile]);

  useEffect(() => {
    if (!isSubscriptionFlow) return;
    if (refreshing) return;
    if (hasActiveSubscription) {
      const timer = setTimeout(() => navigate('/discover'), 2500);
      return () => clearTimeout(timer);
    }
  }, [isSubscriptionFlow, refreshing, hasActiveSubscription, navigate]);

  const headline = isSubscriptionFlow ? "Subscription Activated!" : "Payment Successful!";
  const description = isSubscriptionFlow
    ? "Thank you for becoming a Lsnto subscriber. We're refreshing your account now."
    : "Thank you for your purchase. Your payment has been processed successfully.";

  const primaryCta = isSubscriptionFlow ? () => navigate('/discover') : () => navigate('/dashboard');
  const primaryLabel = isSubscriptionFlow ? "Start Listening" : "Go to Dashboard";

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
        <CheckCircleIcon 
          sx={{ 
            fontSize: 80, 
            color: 'success.main', 
            mb: 2 
          }} 
        />
        
        <Typography variant="h4" component="h1" gutterBottom color="success.main">
          {headline}
        </Typography>
        
        <Typography variant="body1" paragraph color="text.secondary">
          {description}
        </Typography>

        {sessionId && (
          <Card sx={{ mb: 3, backgroundColor: 'grey.50' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Payment Details
              </Typography>
              <Typography variant="body2">
                Session ID: {sessionId}
              </Typography>
              {user && (
                <Typography variant="body2">
                  Customer: {user.email}
                </Typography>
              )}
              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                {isSubscriptionFlow ? "Your subscription is being activated." : "Your Soundcheck access has been activated!"}
              </Typography>
            </CardContent>
          </Card>
        )}

        {!sessionId && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {isSubscriptionFlow
              ? "You're all set! We'll take you to Discover in just a moment."
              : "Payment completed successfully! Your Soundcheck access is now active."}
          </Alert>
        )}

        {isSubscriptionFlow && refreshing && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Syncing your new subscription. This will only take a moment.
          </Alert>
        )}

        {refreshError && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {refreshError}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button 
            variant="contained" 
            onClick={primaryCta}
            sx={{ minWidth: '140px' }}
          >
            {primaryLabel}
          </Button>
          <Button 
            variant="outlined" 
            onClick={() => navigate('/')}
            sx={{ minWidth: '140px' }}
          >
            Back to Home
          </Button>
        </Box>

        <Box sx={{ mt: 4, p: 2, backgroundColor: 'success.light', borderRadius: 1, color: 'success.contrastText' }}>
          <Typography variant="caption">
            <strong>Success!</strong> Your payment was processed securely through Stripe.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default PaymentSuccessPage; 
